import { withFormik } from 'formik';
import * as yup from 'yup';
import SparkTelephonyDeployment from './SparkTelephonyDeployment';
import TelephonyDeploymentRequest from '../../../../shared/models/telephonyDeployment/TelephonyDeploymentRequest';
import { defaultTelephonyDeploymentSettings } from '../../../../shared/constants/telephonyConstants.tsx/defaultTelephonyDeploymentSettings';
import { AppendedSection } from './ApplicationManagerContent';
import * as voicifyApi from '../../../../shared/api';
import { spanishTtsVoice, englishMultilingualTtsVoice } from '../../../../shared/constants/sparkConstants/voices';
import { OutboundSchedulePolicy, defaultOutboundSchedulePolicy, runOutboundProvisioning } from '../../services/outboundAutomation';
import { findVoiceOptionByValue, getVoiceStringForLocale } from './sparkVoiceLanguage';

export interface SparkTelephonyDeploymentProps {
    voiceOptions: any
    handlePreviousSection: () => void
    handleNextSection: () => void
    createTelephonyDeployment: (appId: string, telephonyDeploymentRequest: TelephonyDeploymentRequest) => Promise<any>
    applicationId: string
    environmentId: string
    appendedSections: AppendedSection[]
    telephonyDeploymentSection: AppendedSection
    setAppendedSections: (sections: AppendedSection[]) => void
    incompleteSections: any[]
    setIncompleteSections: (sections: any[]) => void
    getVoiceString: (voice: string) => string
    clearTelephonyDeploymentErrors: () => void
    setPageErrorText: (text: string) => void
    phoneNumberDeploymentErrorText: string
    setPhoneNumberDeploymentErrorText: (text: string) => void
    getTabs: (existingTelephonyDeployment?: boolean) => void
    setTabs: (tabs: any) => void
    loadTelephonyDeployments: () => void
    handleRemoveSection: (sectionToRemove: AppendedSection) => void
    removeSectionFromIncompleteSections: (sectionToRemove: AppendedSection) => void
    assistantType?: string
    orgName?: string
    appName?: string
    onMultilingualEnabled?: () => void
    isOutboundEnabledOnAnyDeployment?: boolean
    orgId?: string
    applicationFeatureFlags?: FeatureFlagModel[]
}

export interface SparkTelephonyDeploymentFormData {
    phoneNumberStartsWith: string
    callRecordingEnabled: boolean
    ttsVoice: string
    useVirtualNumber: boolean
    virtualNumber?: string
    addSpanishLine: boolean
    addOutbound: boolean
    outboundSaved: boolean
    outboundSchedulePolicy: OutboundSchedulePolicy
    outboundCallingEnabled: boolean
}

export const getDefaultVoice = (assistantType?: string): string => {
    if (assistantType === 'ai-ordering') return 'Hope';
    if (assistantType === 'dental-scheduling') return 'Callie';
    return 'Hope';
};

export const submitSparkTelephonyDeployment = async (values: SparkTelephonyDeploymentFormData, props: SparkTelephonyDeploymentProps) => {

    // Build the en-US deployment name
    const enUsName = (props.orgName && props.appName)
        ? `${props.orgName} - ${props.appName} - EN`
        : defaultTelephonyDeploymentSettings.name;

    const isDental = props.assistantType === 'dental-scheduling';
    const isOrdering = props.assistantType === 'ai-ordering';
    const selectedVoiceOption = findVoiceOptionByValue(props.voiceOptions, values.ttsVoice);
    const configuredEnglishVoice = getVoiceStringForLocale(
        selectedVoiceOption,
        'en-US',
        values.addSpanishLine
            ? englishMultilingualTtsVoice
            : (props.getVoiceString(values.ttsVoice) || defaultTelephonyDeploymentSettings.ttsOptions[0].ttsVoice)
    );
    const configuredSpanishVoice = getVoiceStringForLocale(selectedVoiceOption, 'es-US', spanishTtsVoice);

    const telephonyDeploymentRequest: TelephonyDeploymentRequest = {
        environmentId: props.environmentId,
        noTracking: defaultTelephonyDeploymentSettings.noTracking,
        phoneNumberStartsWith: values.useVirtualNumber ? null : (values.phoneNumberStartsWith ?? defaultTelephonyDeploymentSettings.phoneNumberStartsWith),
        ttsOptions: [{
            ttsProvider: defaultTelephonyDeploymentSettings.ttsOptions[0].ttsProvider,
            ttsVoice: configuredEnglishVoice
        }],
        locale: defaultTelephonyDeploymentSettings.locale,
        asrEndpointing: defaultTelephonyDeploymentSettings.asrEndpointing,
        asrProvider: defaultTelephonyDeploymentSettings.asrProvider,
        // When adding Spanish line, upgrade en-US to nova-3
        asrModel: values.addSpanishLine ? 'nova-3' : defaultTelephonyDeploymentSettings.asrModel,
        asrTier: defaultTelephonyDeploymentSettings.asrTier,
        sonioxContextJson: defaultTelephonyDeploymentSettings.sonioxContextJson,
        performLanguageDetection: defaultTelephonyDeploymentSettings.performLanguageDetection,
        dtmfTurnsToListen: values.addSpanishLine ? 2 : defaultTelephonyDeploymentSettings.dtmfTurnsToListen,
        ttsPronunciationsMap: defaultTelephonyDeploymentSettings.ttsPronunciationsMap,
        firstMessageDelayMS: defaultTelephonyDeploymentSettings.firstMessageDelayMS,
        mergeTurnsAfterPauses: defaultTelephonyDeploymentSettings.mergeTurnsAfterPauses,
        mergeTurnTimeoutMS: defaultTelephonyDeploymentSettings.mergeTurnTimeoutMS,
        disableBargeIn: defaultTelephonyDeploymentSettings.disableBargeIn,
        repromptAttempts: defaultTelephonyDeploymentSettings.repromptAttempts,
        repromptDelaySeconds: defaultTelephonyDeploymentSettings.repromptDelaySeconds,
        name: enUsName,
        proactiveResponseDelayMS: isDental ? 1900 : defaultTelephonyDeploymentSettings.proactiveResponseDelayMS,
        proactiveResponses: defaultTelephonyDeploymentSettings.proactiveResponses,
        useProactiveResponses: defaultTelephonyDeploymentSettings.useProactiveResponses,
        disableWelcomeMessageBargeIn: defaultTelephonyDeploymentSettings.disableWelcomeMessageBargeIn,
        disableSpeechStarted: defaultTelephonyDeploymentSettings.disableSpeechStarted,
        speechStartedUnconfirmedBargeInLimit: defaultTelephonyDeploymentSettings.speechStartedUnconfirmedBargeInLimit,
        speechStartedPauseMS: defaultTelephonyDeploymentSettings.speechStartedPauseMS,
        speechStartedRequiredBufferCount: defaultTelephonyDeploymentSettings.speechStartedRequiredBufferCount,
        callRecordingEnabled: values.callRecordingEnabled ?? defaultTelephonyDeploymentSettings.callRecordingEnabled,
        speechStartedLevel: defaultTelephonyDeploymentSettings.speechStartedLevel,
        virtualNumber: values.useVirtualNumber && defaultTelephonyDeploymentSettings.virtualNumber,
        useVirtualNumber: values.useVirtualNumber,
        useGenerativeProactiveResponse: defaultTelephonyDeploymentSettings.useGenerativeProactiveResponse,
        generativeProactiveResponsePrompt: isDental
            ? '{{constant.generative_proactive_response_dental}}'
            : isOrdering
                ? '{{constant.proactive_response_prompt}}'
                : defaultTelephonyDeploymentSettings.generativeProactiveResponsePrompt,
        lastProactiveResponsesToDisallow: defaultTelephonyDeploymentSettings.lastProactiveResponsesToDisallow,
        notificationEmails: defaultTelephonyDeploymentSettings.notificationEmails,
        platform: defaultTelephonyDeploymentSettings.platform,
        postProactiveResponsePauseDelayMS: isDental ? 500 : defaultTelephonyDeploymentSettings.postProactiveResponsePauseDelayMS,
        enableBackgroundOverlayAudio: defaultTelephonyDeploymentSettings.enableBackgroundOverlayAudio,
        backgroundOverlayAudioUrl: defaultTelephonyDeploymentSettings.backgroundOverlayAudioUrl,
        backgroundOverlayAudioLeadInMS: defaultTelephonyDeploymentSettings.backgroundOverlayAudioLeadInMS,
    };

    const createTelephonyDeploymentResponse = await props.createTelephonyDeployment(props.applicationId, telephonyDeploymentRequest);

    if (createTelephonyDeploymentResponse?.resultType === 'Ok' && createTelephonyDeploymentResponse.data) {
        if (values.addSpanishLine) {
            const provisionedPhoneNumber = createTelephonyDeploymentResponse.data.phoneNumber;

            try {
                const languagesResult = await voicifyApi.getAllLanguages();
                if (!languagesResult || languagesResult.resultType !== 'Ok' || !languagesResult.data) {
                    props.setPageErrorText("Phone number created, but Spanish line setup failed: could not fetch language list.");
                } else {
                    const enUsLanguage = languagesResult.data.find(l => l.shortCode === 'en-US');
                    const esUsLanguage = languagesResult.data.find(l => l.shortCode === 'es-US');

                    if (!enUsLanguage || !esUsLanguage) {
                        props.setPageErrorText("Phone number created, but Spanish line setup failed: could not find en-US or es-US language.");
                    } else {
                        try {
                            await voicifyApi.updateApplicationLanguages(props.applicationId, [enUsLanguage.id, esUsLanguage.id]);
                        } catch {
                            props.setPageErrorText("Phone number created, but Spanish line setup failed at language update step.");
                        }

                        try {
                            await voicifyApi.syncNLPAndInteractionModel(props.applicationId, [esUsLanguage.id], enUsLanguage.id);
                        } catch {
                            props.setPageErrorText("Phone number created, but Spanish line setup failed at NLP sync step.");
                        }

                        try {
                            await voicifyApi.queueDeployments(props.applicationId);
                        } catch {
                            props.setPageErrorText("Phone number created, but Spanish line setup failed at NLP queue step.");
                        }

                        const esUsName = (props.orgName && props.appName)
                            ? `${props.orgName} - ${props.appName} - ES`
                            : '';

                        const esUsRequest: TelephonyDeploymentRequest = {
                            ...createTelephonyDeploymentResponse.data,
                            phoneNumberStartsWith: provisionedPhoneNumber,
                            locale: 'es-US',
                            asrModel: 'nova-2',
                            performLanguageDetection: false,
                            dtmfTurnsToListen: 0,
                            name: esUsName,
                            ttsOptions: [{
                                ttsProvider: 'ElevenLabs',
                                ttsVoice: configuredSpanishVoice
                            }],
                            proactiveResponses: 'Un momento|Veamos|Solo un momento|Espera un segundo|Espera mientras reviso eso|Dame un momento|Espera mientras miro eso',
                            followUpProactiveResponses: 'Todavía estoy revisando eso | Sigo investigando esto por ti | Estoy trabajando activamente en esto y te actualizaré pronto | Todavía estoy buscando | Estoy avanzando con esto y te mantendré informado',
                            useGenerativeProactiveResponse: true,
                            generativeProactiveResponsePrompt: isOrdering
                                ? '{{constant.generative_proactive_response_ordering_spanish}}'
                                : '{{constant.generative_proactive_response_dental_spanish}}',
                            maxProactiveResponseAttempts: 3,
                        };

                        try {
                            await props.createTelephonyDeployment(props.applicationId, esUsRequest);
                        } catch {
                            props.setPageErrorText("Phone number created, but Spanish line deployment failed. Please try adding it manually.");
                        }
                    }
                }
            } catch {
                props.setPageErrorText("Phone number created, but Spanish line setup encountered an unexpected error.");
            }

            props.onMultilingualEnabled?.();
        }

        await props.loadTelephonyDeployments();

        if (values.addOutbound && props.assistantType === 'dental-scheduling') {
            const outboundResult = await runOutboundProvisioning({
                applicationId: props.applicationId,
                environmentId: props.environmentId || '',
                organizationName: props.orgName || '',
                locationName: props.appName || '',
                appName: props.appName || '',
                schedulePolicy: values.outboundSchedulePolicy || defaultOutboundSchedulePolicy,
                hasSpanish: values.addSpanishLine,
                outboundCallingEnabled: values.outboundCallingEnabled,
                orgId: props.orgId
            });

            if (!outboundResult.success) {
                props.setPageErrorText(outboundResult.errorMsg || 'We could not save the outbound setup. Please try again.');
                return;
            }

            await props.loadTelephonyDeployments();
        }

        const updatedTabs = props.getTabs(true);
        props.setTabs(updatedTabs);

        const copyAppendedSections = [...props.appendedSections];
        const updatedSections = copyAppendedSections.filter(section => section.id !== props.telephonyDeploymentSection.id);
        props.setAppendedSections(updatedSections);
    };

    if (createTelephonyDeploymentResponse?.resultType !== 'Ok') {
        const incompleteTelephonyDeploymentSection = { ...props.telephonyDeploymentSection, isComplete: false };
        if (createTelephonyDeploymentResponse.errors[0] === "No numbers found") {
            props.setPageErrorText("Uh oh! It looks like the area code you chose isn't available. Please save your changes, exit, and reopen the form trying a different one.");
        } else {
            props.setPageErrorText("Uh oh! It looks like there was a problem creating your phone number. Please save your changes, exit, and try again in a few minutes.");
        }
        const telephonyDeploymentSectionIndex = props.incompleteSections?.findIndex((section) => {
            return section.id === incompleteTelephonyDeploymentSection.id
        });
        const incompleteSectionsCopy = [...props.incompleteSections];
        if (telephonyDeploymentSectionIndex !== -1) {
            incompleteSectionsCopy[telephonyDeploymentSectionIndex] = incompleteTelephonyDeploymentSection;
            props.setIncompleteSections(incompleteSectionsCopy);
        } else {
            props.setIncompleteSections([...incompleteSectionsCopy, incompleteTelephonyDeploymentSection]);
        }
    }
};

const SparkTelephonyDeploymentForm = withFormik<SparkTelephonyDeploymentProps, SparkTelephonyDeploymentFormData>({
    mapPropsToValues: props => (
        {
            phoneNumberStartsWith: '',
            callRecordingEnabled: true,
            ttsVoice: getDefaultVoice(props.assistantType),
            useVirtualNumber: false,
            virtualNumber: '',
            addSpanishLine: false,
            addOutbound: false,
            outboundSaved: false,
            outboundSchedulePolicy: defaultOutboundSchedulePolicy,
            outboundCallingEnabled: false
        }
    ),
    validationSchema: yup.object().shape({
        outboundSaved: yup.boolean().test(
            'outbound-validation',
            'An outbound schedule is required to continue. Either uncheck "Add Outbound" or "Set the Schedule" to proceed.',
            function (value) {
                const { addOutbound } = this.parent;
                if (addOutbound && !value) {
                    return false;
                }
                return true;
            }
        )
    }),
    handleSubmit: async (values, { props }) => submitSparkTelephonyDeployment(values, props)
})(SparkTelephonyDeployment);


export default SparkTelephonyDeploymentForm;
